import React, { useState, useEffect, useMemo, useCallback } from "react";
import { trackError } from "../../../../lib/metrics";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { ModalRegister, Modal } from "../../../../components/ui/ModalRegister";
import { EmployeeRegister } from "../../../origami/components/puzzles/EmployeeRegister";
import { ANIMALS } from "../../../../data/origami/animals";
import { POSTITS } from "../../../../data/origami/postits_player2";
import { MARIE_PAGES, SAMIR_PAGES } from "../../../../data/origami/notebook_pages";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import NotebookViewer from "../../../origami/components/puzzles/NotebookViewer";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { getInspectKeys } from "../../../../data/inspectables";
import HintsModalHub from "../../../../components/ui/HintsModalHub";
import OrigamiCommunicationModal from "../../../origami/components/ui/OrigamiCommunicationModal";
import { useOrigamiReboot } from "../../../../hooks/useOrigamiReboot";
import { rebootText } from "../../../../data/origami/origamiTexts";

/**
 * Stage 1 — Joueur 2 — Composant partagé (4J / 5J / 6J)
 *
 * Props spécifiques :
 *   playerCount  {number}   — 4, 5 ou 6. Détermine getInspectKeys.
 *   showRegister {boolean}  — true en 4J/5J, false en 6J.
 *
 * Modifications v2 :
 *   - Suppression du carnet d'Alex (aucune info utile, pur bruit)
 *   - Tableau blanc réduit : 3 points clés orientés vers l'action
 *   - Chaises conservées (easter egg)
 */
export default function Stage1Player2Room({
  code,
  playerId,
  hintSignal,
  raisedCount,
  raisedPlayers,
  centerMaxH,
  pseudo,
  playerCount,
  showRegister,
}) {
  const INSPECT_KEYS = useMemo(() => {
    return getInspectKeys({ gameId: 1, playerCount, stage: 1, seat: 2 });
  }, [playerCount]);

  // — Modales —
  const [showNotebookMarie, setShowNotebookMarie] = useState(false);
  const [showNotebookSamir, setShowNotebookSamir] = useState(false);
  const [showWhiteboardModal, setShowWhiteboardModal] = useState(false);
  const [showPostitsModal, setShowPostitsModal] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showChairModal, setShowChairModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // — Chaises —
  const [inspectedChairs, setInspectedChairs] = useState(new Set());
  const chairInspectCount = inspectedChairs.size;

  // — Carnet Samir —
  const [samirLocked, setSamirLocked] = useState(true);
  const [showSamirLockModal, setShowSamirLockModal] = useState(false);
  const [samirCodeInput, setSamirCodeInput] = useState("");
  const [samirCodeError, setSamirCodeError] = useState("");
  const [samirCooldownUntil, setSamirCooldownUntil] = useState(null);

  // — Suivi progression (pour indices) —
  const [openedMarie, setOpenedMarie] = useState(false);
  const [openedSamir, setOpenedSamir] = useState(false);

  // — Post-its —
  const [postitIndex, setPostitIndex] = useState(0);
  const [colorblindModePostitsP2, setColorblindModePostitsP2] = useState(false);

  // — Registre —
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // — BG —
  const [bgReady, setBgReady] = useState(false);

  // — Hooks —
  const { inspectedCount, total } = useInspectProgress({
    sessionCode: code, stage: 1, playerId: Number(playerId), keys: INSPECT_KEYS,
  });
  const { showOrigamiReboot, setShowOrigamiReboot } = useOrigamiReboot({ code, playerId });
  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 1 });

  const closeHints = useCallback(() => setShowHintModal(false), []);
  const usedStorageKey = useMemo(() => `hints_used:${code}:s1:p${playerId}`, [code, playerId]);

  // — Assets —
  const backgroundUrl = "/assets/s1-p2-room.webp";
  const notebookMarieImg = "/assets/p2_notebook_marie.webp";
  const notebookSamirImg = "/assets/p2_notebook_samir.webp";
  const whiteboardImg = "/assets/p2_whiteboard.webp";
  const postitsImg = "/assets/p2_postits.webp";
  const rhRegister = "/assets/p2_rh_register.webp";

  useEffect(() => { setBgReady(false); }, [backgroundUrl]);
  useEffect(() => { if (!hintSignal) return; setShowHintModal(true); }, [hintSignal]);

  // — Persistance colorblind —
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`p2:postits:colorblind:${code}:${playerId}`);
      if (saved === "1") setColorblindModePostitsP2(true);
    } catch { }
  }, [code, playerId]);

  useEffect(() => {
    try {
      localStorage.setItem(`p2:postits:colorblind:${code}:${playerId}`, colorblindModePostitsP2 ? "1" : "0");
    } catch { }
  }, [colorblindModePostitsP2, code, playerId]);

  // — Chaises —
  const handleInspectChair = (chairId) => {
    setShowChairModal(true);
    setInspectedChairs((prev) => {
      if (prev.has(chairId)) return prev;
      const updated = new Set(prev);
      updated.add(chairId);
      return updated;
    });
  };

  // — Cadenas Samir —
  const handleSamirCodeSubmit = (e) => {
    e.preventDefault();

    if (samirCooldownUntil && Date.now() < samirCooldownUntil) {
      const remainingSec = Math.ceil((samirCooldownUntil - Date.now()) / 1000);
      setSamirCodeError(`⏱️ Le cadenas est temporairement bloqué. Réessayer dans ${remainingSec}s...`);
      return;
    }

    const trimmed = samirCodeInput.trim();

    if (trimmed === "2593") {
      setSamirLocked(false);
      setSamirCodeError("");
      setShowSamirLockModal(false);
      setShowNotebookSamir(true);
      setOpenedSamir(true);
      missionResolve({ key: "s1.p2.samir.found" });
      missionResolve({ key: "s1.p2.postItsWall.found" });
      missionResolve({ key: "s1.p3.postItsOrder.found" });
    } else {
      trackError({ sessionCode: code, stage: 1, playerId, reason: "stage1_samir_notebook_wrong_code", meta: { inputLength: trimmed.length } });
      setSamirCooldownUntil(Date.now() + 15000);
      setSamirCodeError("❌ Code incorrect. Le cadenas s'est verrouillé 15 secondes par sécurité.");
    }
  };

  useEffect(() => {
    if (!samirCooldownUntil || Date.now() >= samirCooldownUntil) return;
    const interval = setInterval(() => {
      if (Date.now() >= samirCooldownUntil) {
        setSamirCooldownUntil(null);
        setSamirCodeError("");
        clearInterval(interval);
      } else {
        const remainingSec = Math.ceil((samirCooldownUntil - Date.now()) / 1000);
        setSamirCodeError(`⏱️ Le cadenas est temporairement bloqué. Réessayer dans ${remainingSec}s...`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [samirCooldownUntil]);

  // — Hints —
  const hintCategories = useMemo(() => {
    const cats = [
      {
        id: "notebooks",
        title: "Carnets",
        subtitle: "Où chercher les infos utiles",
        hintKeyPrefix: "s1:p2:notebooks",
        levels: {
          l1: (<><p className="mb-2">Cette salle de réunion a l&apos;air banale… mais les carnets sur la table ne sont pas là par hasard.</p><p className="text-xs text-gray-300">Inspecte les carnets. Les habitudes de mots de passe se cachent souvent dans des notes perso.</p></>),
          l2: (<><p className="mb-2">Dans les carnets, ne cherche pas un "mot de passe écrit en clair". Cherche plutôt des choses qui reviennent : habitudes, prénoms, animaux, petites obsessions…</p></>),
          l3: (<><p className="mb-2">Une fois que tu as accès aux carnets, recoupe avec le registre RH. L'objectif est de relier une personne à un indice "perso".</p><p className="text-xs text-gray-300">Mets de côté : le nom du chat de Marie, et le nom du chien de Samir. Ce sont des infos "faciles à deviner"… donc typiquement utilisées comme mot de passe.</p><div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3"><p className="text-[11px] text-slate-300">Progression : <span className="font-semibold text-slate-100">{openedMarie ? "Marie ✅" : "Marie ❌"}</span> · <span className="font-semibold text-slate-100">{openedSamir ? "Samir ✅" : "Samir ❌"}</span></p></div></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Carnets)</p><ul className="mt-2 text-xs text-gray-200 list-disc pl-5 space-y-1"><li>Les carnets contiennent des indices "perso" (chat / chien) et des habitudes qui servent de mots de passe.</li><li>Tu dois recouper ça avec le registre RH (ID → personne).</li><li>Les infos clés à retrouver : le chat de Marie, et le chien de Samir.</li></ul></>),
        },
      },
      {
        id: "postits",
        title: "Mur de post-its",
        subtitle: "Déverrouiller le carnet de Samir",
        hintKeyPrefix: "s1:p2:postits",
        levels: {
          l1: (<><p className="mb-2">Le carnet de Samir est verrouillé… et il mentionne explicitement les post-its.</p><p className="text-xs text-gray-300">Les post-its ne sont pas juste des rappels d'agenda : certains éléments sont mis en avant.</p></>),
          l2: (<><p className="mb-2">Le code est une suite de <span className="italic">4 chiffres</span>.</p><p className="text-xs text-gray-300">Sur le mur, repère les chiffres qui ressortent.</p></>),
          l3: (<><p className="mb-2">Si tu es sur(e) d'avoir tout lu sur le mur mais que tu n'arrives pas à conclure : l'ordre te sera indiqué par un autre joueur...</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Cadenas)</p><p className="mt-2 text-xs text-gray-200">Code du cadenas : <span className="font-bold text-amber-200">2593</span></p></>),
        },
      },
    ];

    if (showRegister) {
      cats.push({
        id: "register",
        title: "Registre RH",
        subtitle: "Comment l'utiliser",
        hintKeyPrefix: "s1:p2:register",
        levels: {
          l1: (<><p className="mb-2">Le registre RH sert à relier un ID à une personne.</p><p className="text-xs text-gray-300">Il y a un ordinateur quelque part affiché comme appartenant à l'utilisateur ID 359.</p></>),
          l2: (<><p className="mb-2">Cherche l'ID 359 dans le registre pour savoir à qui appartient cet ordinateur.</p></>),
          l3: (<><p className="mb-2">Une fois l&apos;ID trouvé, tu sais <span className="italic">qui</span> est la cible. Il te reste à trouver son mot de passe personnel dans son carnet.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Registre RH)</p><ul className="mt-2 text-xs text-gray-200 list-disc pl-5 space-y-1"><li>ID 359 → <span className="font-semibold">Marie Dupont</span></li><li>Ensuite, cherche le nom de son chat dans son carnet.</li></ul></>),
        },
      });
    }

    return cats;
  }, [openedMarie, openedSamir, showRegister]);

  // — Tableau blanc réduit et actionnable —
  const WhiteboardContent = () => (
    <div className="flex justify-center">
      <div className="relative w-full max-w-2xl bg-slate-900/40 rounded-2xl border border-white/10 p-4">
        <div className="mx-auto bg-white rounded-xl shadow-xl border border-slate-300 px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold tracking-wide text-slate-800">Réunion sécu — Points clés</h4>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Origami – interne</span>
          </div>
          <ul className="space-y-3 text-sm text-slate-800">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-red-500 font-bold shrink-0">⚠</span>
              <span>Les mots de passe basés sur un <span className="font-semibold underline">animal de compagnie</span> sont les premiers testés par les attaquants. Si c'est le nom de ton chat ou de ton chien, change-le.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-amber-500 font-bold shrink-0">→</span>
              <span>L'ordinateur de l'utilisateur <span className="font-mono font-semibold">ID 359</span> est une cible prioritaire. Son mot de passe doit être retrouvé et changé urgemment.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-slate-400 font-bold shrink-0">📋</span>
              <span className="text-slate-500 italic text-xs">Pour identifier qui est l'utilisateur 359 : consulter le registre RH.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  // — Rendu —
  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div
        className="relative h-screen w-auto mx-auto aspect-square"
        style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}
      >
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Salle de réunion
        </div>

        <img src={backgroundUrl} alt="Salle de réunion"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)} onError={() => setBgReady(true)}
        />

        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>

          {/* Carnet Marie */}
          <img src={notebookMarieImg} alt="Carnet de notes - Marie"
            onClick={() => {
              markInspectedOnce('stage1player2:notebookMarie');
              setShowNotebookMarie(true);
              setOpenedMarie(true);
              missionUnlock({ key: "s1.p2.marie.found", text: `${pseudo} a trouvé le carnet de Marie. Elle parle beaucoup de son chat.` });
            }}
            className="p2-notebook-marie absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* Carnet Samir */}
          <img src={notebookSamirImg} alt="Carnet de notes - Samir"
            onClick={() => {
              if (samirLocked) {
                markInspectedOnce('stage1player2:notebookSamir');
                setShowSamirLockModal(true);
                missionUnlock({ key: "s1.p2.samir.found", text: `${pseudo} a trouvé le carnet de Samir. Il est verrouillé.` });
              } else {
                markInspectedOnce('stage1player2:notebookSamir');
                setShowNotebookSamir(true);
                setOpenedSamir(true);
              }
            }}
            className="p2-notebook-samir absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* Tableau blanc */}
          <img src={whiteboardImg} alt="Tableau blanc"
            onClick={() => { markInspectedOnce('stage1player2:whiteboard'); setShowWhiteboardModal(true); }}
            className="p2-whiteboard absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* Registre RH — 4J/5J uniquement */}
          {showRegister && (
            <img src={rhRegister} alt="Registre RH"
              onClick={() => {
                markInspectedOnce('stage1player2:rhRegister');
                setShowRegisterModal(true);
                missionUnlock({ key: "s1.p2.rhRegister.found", text: `${pseudo} a trouvé le registre des employés.` });
              }}
              className="p2-rh_register absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}

          {/* Mur de post-its */}
          <img src={postitsImg} alt="Mur de post-its"
            onClick={() => {
              markInspectedOnce('stage1player2:postItsWall');
              setShowPostitsModal(true);
              missionUnlock({ key: "s1.p2.postItsWall.found", text: `${pseudo} a trouvé un mur rempli de post-its colorés.` });
            }}
            className="p2-postits absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* Chaises */}
          {["1", "2", "3", "4", "5"].map((n) => (
            <img key={n} src={`/assets/p2_chair${n}.webp`} alt="Chaise de réunion"
              onClick={() => { markInspectedOnce(`stage1player2:chair${n}`); handleInspectChair(`chair-${n}`); }}
              className={`p2-chair-${n} absolute cursor-pointer hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]`}
            />
          ))}
        </div>
      </div>

      {/* === MODALES === */}

      {/* Cadenas Samir */}
      <Modal open={showSamirLockModal} title="Carnet de Samir — verrouillé"
        onClose={() => { setShowSamirLockModal(false); setSamirCodeError(""); }}
        onHint={() => setShowHintModal(true)}
      >
        <p className="mb-2">Un petit cadenas à code bloque l&apos;élastique du carnet. Sur la couverture, quelqu&apos;un a griffonné :</p>
        <p className="text-xs italic text-amber-200 mb-3">J'ai tout noté sur les post-its, mais personne ne trouvera de toute façon !</p>
        <form onSubmit={handleSamirCodeSubmit} className="space-y-2 text-xs">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-slate-400">Code (4 chiffres)</label>
          <div className="flex gap-2">
            <input type="text" maxLength={4} value={samirCodeInput}
              onChange={(e) => { setSamirCodeInput(e.target.value.replace(/[^0-9]/g, "")); setSamirCodeError(""); }}
              className="flex-1 px-3 py-2 rounded-lg bg-black/60 border border-white/20 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono tracking-[0.25em]"
              placeholder="____"
            />
            <button type="submit"
              disabled={!!(samirCooldownUntil && Date.now() < samirCooldownUntil)}
              className={["px-4 py-2 rounded-lg text-xs font-semibold transition", (samirCooldownUntil && Date.now() < samirCooldownUntil) ? "bg-slate-600 cursor-not-allowed opacity-50" : "bg-indigo-600 hover:bg-indigo-500"].join(" ")}
            >Valider</button>
          </div>
          {samirCodeError
            ? <p className="text-[11px] text-red-400">{samirCodeError}</p>
            : <p className="text-[11px] text-gray-400 mt-1">Les post-its ne sont peut-être pas que des rappels d&apos;agenda…</p>
          }
        </form>
      </Modal>

      {/* Carnets */}
      <Modal open={showNotebookMarie} title="Carnet de Marie" onClose={() => setShowNotebookMarie(false)} onHint={() => setShowHintModal(true)}>
        <NotebookViewer pages={MARIE_PAGES} hintStorageKey={`hint:pageflip:${code}:marie`} />
      </Modal>
      <Modal open={showNotebookSamir} title="Carnet de Samir" onClose={() => setShowNotebookSamir(false)} onHint={() => setShowHintModal(true)}>
        <NotebookViewer pages={SAMIR_PAGES} hintStorageKey={`hint:pageflip:${code}:samir`} />
      </Modal>

      {/* Tableau blanc — version réduite */}
      <ModalRegister open={showWhiteboardModal} title="Tableau blanc – Réu sécu" onClose={() => setShowWhiteboardModal(false)}>
        <WhiteboardContent />
      </ModalRegister>

      {/* Registre RH */}
      {showRegister && (
        <ModalRegister open={showRegisterModal} title="Registre RH — Annuaire interne"
          onClose={() => { setShowRegisterModal(false); setSelectedEmployee(null); }}
          onHint={() => setShowHintModal(true)}
        >
          <p className="text-[11px] text-slate-400 mb-3 italic">Correspondance ID ↔ Employé(e)</p>
          <p className="text-[11px] text-gray-400 mb-3">Clique sur les informations d&apos;un employé pour accéder à plus de détails.</p>

          <div className="mb-3 rounded-lg bg-black/40 border border-white/15 p-3 text-xs">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-300 mb-1">Annexe – animaux de compagnie déclarés</p>
            <p className="text-[11px] text-slate-300 mb-2">Une petite feuille est agrafée à la fin du registre avec une liste d&apos;animaux… mais la colonne "propriétaire" est illisible.</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] text-slate-100">
              {ANIMALS.map((name) => (<li key={name}>• {name}</li>))}
            </ul>
          </div>

          {selectedEmployee && (
            <div className="mt-3 rounded-lg bg-black/40 border border-white/10 p-3 text-xs">
              <p className="text-[11px] text-slate-400 mb-1 uppercase tracking-[0.16em]">Détails</p>
              <p className="text-sm font-semibold">{selectedEmployee.prenom} {selectedEmployee.nom}</p>
              <p className="text-[11px] text-slate-300">ID {selectedEmployee.id} — {selectedEmployee.poste} ({selectedEmployee.equipe})</p>
              <p className="mt-2 text-[11px] text-slate-400 italic">{selectedEmployee.notes}</p>
            </div>
          )}

          <EmployeeRegister
            onEmployeeSelect={setSelectedEmployee}
            onEmployeeClick={(emp) => {
              if (Number(emp?.id) === 359) {
                missionUnlock({ key: "s1.p2.employeeId.found", text: `${pseudo} a trouvé que l'ID 359 est celui de Marie Dupont.` });
              }
            }}
          />
        </ModalRegister>
      )}

      {/* Mur de post-its */}
      <Modal open={showPostitsModal} title="Mur de post-its" onClose={() => setShowPostitsModal(false)} onHint={() => setShowHintModal(true)}>
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-sm text-gray-200">Une série de petits post-its de couleur…</p>
          <button type="button"
            onClick={() => setColorblindModePostitsP2((v) => !v)}
            className={["rounded-full px-3 py-1 text-[11px] border transition", colorblindModePostitsP2 ? "bg-indigo-500/15 border-indigo-400/40 text-indigo-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"].join(" ")}
            aria-pressed={colorblindModePostitsP2}
          >
            {colorblindModePostitsP2 ? "Mode daltonien : ON" : "Mode daltonien"}
          </button>
        </div>
        <div className="flex flex-col items-center">
          <div className={["relative p-6 shadow-xl text-black w-64 h-64", POSTITS[postitIndex].color].join(" ")}>
            {colorblindModePostitsP2 && (
              <div className="absolute top-3 right-3 rounded-md bg-black/10 border border-black/10 px-2 py-1">
                <span className="text-xs font-mono font-bold text-black/70">{POSTITS[postitIndex].token}</span>
              </div>
            )}
            <p className="font-handwritten text-lg text-center whitespace-pre-wrap">{POSTITS[postitIndex].text}</p>
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setPostitIndex((i) => (i === 0 ? POSTITS.length - 1 : i - 1))}>◀</button>
            <button onClick={() => setPostitIndex((i) => (i === POSTITS.length - 1 ? 0 : i + 1))}>▶</button>
          </div>
          {colorblindModePostitsP2 && (
            <p className="mt-3 text-[11px] text-slate-300 text-center">Correspondance couleurs: J=Jaune · R=Rose · V=Vert · B=Bleu</p>
          )}
        </div>
      </Modal>

      {/* Chaises */}
      <Modal open={showChairModal} title="Inspection de chaise" onClose={() => setShowChairModal(false)}>
        {chairInspectCount === 1 && <p>Juste une chaise. Rien de spécial à signaler.</p>}
        {chairInspectCount === 2 && <p>Encore une chaise. Tu vérifies dessous, au cas où…</p>}
        {chairInspectCount === 3 && <p>Tu commences à développer une expertise en inspection de chaises, mais toujours rien.</p>}
        {chairInspectCount >= 4 && (
          <div className="space-y-2">
            <p>Sous l&apos;assise, tu trouves finalement un petit papier froissé, coincé dans la structure métallique.</p>
            <p className="mt-2 text-xs italic text-amber-200 whitespace-pre-wrap">&quot;Penser à emprunter le dictionnaire de morse de Samir. Il traîne tout le temps sur son bureau.&quot;</p>
            <p className="text-xs text-gray-300">Ça ressemble à un mémo oublié…</p>
          </div>
        )}
      </Modal>

      {/* Reboot Origami */}
      <OrigamiCommunicationModal open={showOrigamiReboot} text={rebootText}
        onClose={() => setShowOrigamiReboot(false)} autoClose={true} autoCloseDuration={10000}
      />

      {/* Indices */}
      <HintsModalHub open={showHintModal} onClose={closeHints}
        title="Indices — Salle de réunion"
        markHintUsedOnce={markHintUsedOnce} categories={hintCategories}
        usedStorageKey={usedStorageKey}
      />
    </div>
  );
}